import { useTranslation } from 'react-i18next';
import Modal from '@/components/base/Modal';
import type { DriverDocument } from '@/types';

interface DocumentPreviewModalProps {
  open: boolean;
  title: string;
  document: DriverDocument | null;
  onClose: () => void;
}

function isImage(url: string | null): boolean {
  if (!url) return false;
  return url.startsWith('data:image') || /\.(png|jpe?g|webp|gif)$/i.test(url) || url.includes('readdy.ai');
}

export default function DocumentPreviewModal({
  open,
  title,
  document,
  onClose,
}: DocumentPreviewModalProps) {
  const { t } = useTranslation();
  const previewable = document && isImage(document.url);

  return (
    <Modal open={open} onClose={onClose} size="md" title={title}>
      <div className="space-y-3">
        {previewable ? (
          <div className="overflow-hidden rounded-md border border-background-200 bg-background-100">
            <img
              src={document?.url ?? ''}
              alt={document?.fileName ?? title}
              className="max-h-[52vh] w-full object-contain"
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-background-300 bg-background-100/60 px-6 py-10 text-center">
            <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-file-unknow-line text-2xl leading-none" aria-hidden="true" />
            </span>
            <p className="text-sm text-foreground-600">{t('drivers.detail.document.unavailable')}</p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3.5 py-2.5">
          <p className="flex items-start gap-2 text-xs leading-relaxed text-amber-800">
            <i className="ri-alert-line mt-0.5 text-base text-amber-600" aria-hidden="true" />
            {t('drivers.detail.document.demo')}
          </p>
          {document ? (
            <span className="lb-ltr-field truncate text-xs text-foreground-500">{document.fileName}</span>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}